import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { TileDef } from '../../engine/module';
import { Badge, Money, Stat, type Tone } from '../../engine/design/primitives/Misc';
import { formatMinutes } from '../../lib/format';
import { AgingTile, BillableTile, BookedTodayTile, ClientsAttentionTile, GoneQuietTile, listLink, LoggedTile, NeedsTimeTile } from './HomeBands';
import type { HomeTiles } from './types';

// The home module's tiles (ModuleUI.tiles). Each renders from the GET /v1/home aggregate and is
// null when the API omitted its data, so a tile the caller may not see never renders. "Mine"
// tiles follow the scoped technician (the caller unless a manager picked someone else).

export interface BandDef {
  key: string;
  /** Visible heading; the overview band has none. */
  title?: string;
  label: string;
}

export const BANDS: BandDef[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'my-week', title: 'My week', label: 'My week' },
  { key: 'attention', title: 'Needs attention', label: 'Needs attention' },
];

const OPEN = ['new', 'open', 'pending'] as const;
const HEALTH_TONE: Record<string, Tone> = { on_track: 'pos', at_risk: 'warn', off_track: 'neg' };

/** A tile that links to the filtered list behind its number. */
export function Tile({ to, children }: { to?: string; children: ReactNode }) {
  if (!to) return <div>{children}</div>;
  return (
    <Link to={to} className="hf-home-tile" style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
      {children}
    </Link>
  );
}

const tile = (key: string, band: string, label: string, render: (t: HomeTiles) => ReactNode | null): TileDef => ({ key, band, label, render: (h) => render(h as HomeTiles) });
const techOf = (t: HomeTiles) => t.scope?.technician_id ?? t.me.technician_id;
const ownWeek = (t: HomeTiles) => !t.scope?.technician_id || t.scope.technician_id === t.me.technician_id;

export const homeTiles: TileDef[] = [
  tile('home.my-open', 'overview', 'My open tickets', (t) => {
    if (!t.tickets) return null;
    const me = techOf(t);
    const open = OPEN.reduce((s, c) => s + (t.tickets!.mine_by_category[c] ?? 0), 0);
    return (
      <Tile to={me ? listLink('/tickets', [{ field: 'assigned_tech_id', op: 'eq', value: me }]) : '/tickets'}>
        <Stat label={ownWeek(t) ? 'My open tickets' : `${t.scope?.technician_name ?? 'Their'} open tickets`} icon="life-buoy" value={open} sub={me ? OPEN.map((c) => `${t.tickets!.mine_by_category[c] ?? 0} ${c}`).join(' · ') : 'No technician record linked to your login'} />
      </Tile>
    );
  }),
  tile('home.sla', 'overview', 'SLA at risk', (t) =>
    t.tickets ? (
      <Tile to="/tickets/board">
        <Stat label="SLA at risk" icon="alert-triangle" value={t.tickets.sla_at_risk} sub={t.tickets.sla_breached > 0 ? `${t.tickets.sla_breached} breached` : 'None breached'} accent={t.tickets.sla_breached > 0} />
      </Tile>
    ) : null,
  ),
  tile('home.unassigned', 'overview', 'Unassigned', (t) =>
    t.tickets ? (
      <Tile to={listLink('/tickets', [{ field: 'assigned_tech_id', op: 'is_null', value: true }])}>
        <Stat label={t.me.technician_id && !t.connected ? 'Unassigned in my queues' : 'Unassigned'} icon="inbox" value={t.tickets.unassigned_in_my_queues} sub={t.tickets.queues.filter((x) => x.unassigned > 0).map((x) => `${x.name} ${x.unassigned}`).join(' · ') || 'Every ticket has an owner'} />
      </Tile>
    ) : null,
  ),
  tile('home.approvals', 'overview', 'Approvals waiting for me', (t) => {
    if (!t.approvals) return null;
    const first = t.approvals.items[0];
    return (
      <Tile to="/approvals">
        <Stat label="Approvals waiting for me" icon="shield-check" value={t.approvals.waiting} sub={first ? `${first.step_name ?? first.process_name}${first.ticket_ref ? ` on ${first.ticket_ref}` : ''}` : 'Nothing to decide'} />
      </Tile>
    );
  }),
  tile('home.quotes', 'overview', 'Quotes awaiting a decision', (t) =>
    t.quotes ? (
      <Tile to={listLink('/quotes', [{ field: 'status', op: 'in', value: ['sent', 'viewed'] }])}>
        <Stat label="Quotes awaiting a decision" icon="quote" value={t.quotes.awaiting} sub={t.quotes.expiring_this_week > 0 ? `${t.quotes.expiring_this_week} expiring this week` : 'None expiring this week'} />
      </Tile>
    ) : null,
  ),
  tile('home.projects', 'overview', 'Projects in flight', (t) =>
    t.projects ? (
      <Tile to={listLink('/projects', [{ field: 'status', op: 'in', value: ['planned', 'active', 'on_hold'] }])}>
        <Stat label="Projects in flight" icon="folder-open" value={t.projects.active} sub={<span>{(['on_track', 'at_risk', 'off_track'] as const).map((h) => (<Badge key={h} tone={HEALTH_TONE[h]} title={h}>{t.projects!.by_health[h] ?? 0}</Badge>))}</span>} />
      </Tile>
    ) : null,
  ),
  tile('home.my-tasks', 'overview', 'My tasks due this week', (t) =>
    t.projects && t.projects.my_tasks_due_this_week !== null ? (
      <Tile to="/my-tasks">
        <Stat label="My tasks due this week" icon="check-square" value={t.projects.my_tasks_due_this_week} sub={t.projects.tasks[0] ? `${t.projects.tasks[0].ref ?? ''} ${t.projects.tasks[0].title}`.trim() : 'Nothing due'} />
      </Tile>
    ) : null,
  ),
  // A connected system's projects owned by me (RisingTideOS cockpit), each opening its workspace.
  tile('home.my-projects', 'my-week', 'My projects', (t) =>
    t.projects?.mine ? (
      <Tile to={listLink('/projects', [{ field: 'status', op: 'in', value: ['planned', 'active', 'on_hold'] }])}>
        <Stat
          label="My projects"
          icon="folder-open"
          value={t.projects.mine.length}
          sub={t.projects.mine.length ? 'Open projects you own' : 'You own no open projects'}
          preview={t.projects.mine.slice(0, 3).map((p) => (p.client_name ? `${p.name} · ${p.client_name}` : p.name))}
        />
      </Tile>
    ) : null,
  ),
  tile('home.ar', 'overview', 'AR outstanding', (t) => {
    if (!t.finance) return null;
    const ar = t.finance.ar[0];
    const overdue = ar ? (t.finance.overdue.find((o) => o.currency === ar.currency) ?? t.finance.overdue[0]) : t.finance.overdue[0];
    return (
      <Tile to={listLink('/invoices', [{ field: 'status', op: 'in', value: ['issued', 'frozen'] }])}>
        <Stat label="AR outstanding" icon="receipt" accent value={<Money minor={ar?.total_minor ?? 0} currency={ar?.currency ?? 'USD'} />} sub={overdue && overdue.count > 0 ? <span><Money minor={overdue.total_minor} currency={overdue.currency} color /> overdue · {overdue.count} invoice{overdue.count === 1 ? '' : 's'}</span> : 'Nothing past due'} />
      </Tile>
    );
  }),
  tile('home.unbilled', 'overview', 'Unbilled time', (t) => {
    if (!t.finance) return null;
    const unbilled = t.finance.unbilled[0];
    return (
      <Tile to="/billing">
        <Stat label="Unbilled time" icon="timer" value={<Money minor={unbilled?.amount_minor ?? 0} currency={unbilled?.currency ?? t.finance.ar[0]?.currency ?? 'USD'} />} sub={unbilled ? `${formatMinutes(unbilled.minutes)} ready to invoice` : 'All billable time invoiced'} />
      </Tile>
    );
  }),
  // NovaPSA's notifications are not used while a connected system serves the tenant.
  tile('home.notifications', 'overview', 'Unread notifications', (t) => t.connected ? null : (
    <Tile>
      <Stat label="Unread notifications" icon="bell" value={t.notifications.unread} sub={t.notifications.unread > 0 ? 'Open the bell in the top bar' : "You're all caught up"} />
    </Tile>
  )),

  tile('home.booked', 'my-week', 'Booked today', (t) => (t.time ? <BookedTodayTile time={t.time} /> : null)),
  tile('home.billable', 'my-week', 'Billable this week', (t) => (t.time ? <BillableTile time={t.time} /> : null)),
  tile('home.logged', 'my-week', 'Logged this week', (t) => (t.time ? <LoggedTile time={t.time} /> : null)),
  // A connected system cannot tell which past sessions lack time (needs_time null): no tile then.
  tile('home.needs-time', 'my-week', 'Needs time', (t) => (t.time?.needs_time ? <NeedsTimeTile time={t.time} needs={t.time.needs_time} /> : null)),

  tile('home.gone-quiet', 'attention', 'Gone quiet', (t) => (t.attention ? <GoneQuietTile attention={t.attention} /> : null)),
  tile('home.clients-attention', 'attention', 'Clients needing attention', (t) => (t.attention ? <ClientsAttentionTile attention={t.attention} /> : null)),
  tile('home.aging', 'attention', 'Receivables by age', (t) => (t.finance?.aging?.length ? <AgingTile aging={t.finance.aging} /> : null)),
];
